import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";

const ChannelAttributionChart = ({ data }) => {
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    // Check if screen is mobile size
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    // Filter out items with empty or invalid channel names
    const validData = data.filter(
        (item) => item.channels && item.channels.trim() !== ""
    );

    // Channels to group as "Other" on mobile
    const channelsToGroup = [
        "Display",
        "Facebook",
        "Media",
        "Others",
        "Promotional",
        "Referral",
        "Safefra",
        "Shopzilla_cse",
        "Unspecified",
        "Youtube_Video",
        "Social_Other",
        "Affiliate"
    ];

    // Process data differently for mobile vs desktop
    const processedData = isMobile ? (() => {
        // Separate main channels from channels to group
        const mainChannels = validData.filter(item =>
            !channelsToGroup.includes(item.channels)
        );

        const channelsToGroupData = validData.filter(item =>
            channelsToGroup.includes(item.channels)
        );

        // Sum up all the "Other" channels
        if (channelsToGroupData.length > 0) {
            const otherChannelSums = {
                channels: "Other",
                attribution_first_click_heuristic: channelsToGroupData.reduce(
                    (sum, item) => sum + (item.attribution_first_click_heuristic || 0), 0
                ),
                attribution_last_click_heuristic: channelsToGroupData.reduce(
                    (sum, item) => sum + (item.attribution_last_click_heuristic || 0), 0
                ),
                attribution_markov_algorithmic: channelsToGroupData.reduce(
                    (sum, item) => sum + (item.attribution_markov_algorithmic || 0), 0
                ),
                attribution_shapley_size4_conv_rate_algorithmic: channelsToGroupData.reduce(
                    (sum, item) => sum + (item.attribution_shapley_size4_conv_rate_algorithmic || 0), 0
                )
            };

            return [...mainChannels, otherChannelSums];
        }

        return mainChannels;
    })() : validData;

    const filteredData = selectedChannel
        ? processedData.filter((item) => item.channels === selectedChannel)
        : processedData;

    const axisColor = window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666';

    const option = {
        tooltip: {
            trigger: "axis",
            backgroundColor: 'rgba(50, 50, 50, 0.95)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderWidth: 1,
            textStyle: {
                color: '#fff',
                fontSize: isMobile ? 12 : 14
            },
            formatter: (params) => {
                let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
                params.forEach(param => {
                    result += `<div style="margin: 4px 0;">
                        <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                        ${param.seriesName}: <strong>${param.value.toLocaleString()}</strong>
                    </div>`;
                });
                return result;
            }
        },
        legend: {
            top: isMobile ? 10 : 20,
            data: ["First Click", "Last Click", "Data Attribution 1", "Data Attribution 2"],
            textStyle: {
                fontSize: isMobile ? 10 : 12,
                color: axisColor
            },
            itemWidth: isMobile ? 15 : 25,
            itemHeight: isMobile ? 10 : 14,
            itemGap: isMobile ? 8 : 10,
            orient: isMobile ? 'horizontal' : 'horizontal',
            // Split legend into two rows on mobile if needed
            formatter: isMobile ? (name) => {
                const shortNames = {
                    "First Click": "First",
                    "Last Click": "Last",
                    "Data Attribution 1": "Data Attr 1",
                    "Data Attribution 2": "Data Attr 2"
                };
                return shortNames[name] || name;
            } : undefined
        },
        grid: {
            bottom: isMobile ? 120 : 100,
            left: isMobile ? 60 : 60,
            right: isMobile ? 30 : 30,
            top: isMobile ? 50 : 60,
        },
        dataZoom: [
            {
                type: "inside",
                xAxisIndex: 0,
                zoomOnMouseWheel: !isMobile // Disable zoom on mobile to prevent conflicts with scrolling
            },
            ...(isMobile ? [] : [{ type: "slider", xAxisIndex: 0, height: 20, bottom: 10 }])
        ],
        xAxis: {
            type: "category",
            data: filteredData.map((item) => item.channels),
            axisLabel: {
                interval: 0,
                rotate: isMobile ? 45 : 70,
                fontSize: isMobile ? 9 : 12,
                color: axisColor,
                // Better text wrapping for mobile
                formatter: (value) => {
                    if (isMobile && value.length > 8) {
                        return value.slice(0, 8) + '...';
                    }
                    return value;
                },
                margin: isMobile ? 8 : 12
            },
            axisLine: {
                lineStyle: {
                    color: '#e0e0e0'
                }
            },
            axisTick: {
                length: isMobile ? 3 : 5
            }
        },
        yAxis: {
            type: "value",
            axisLabel: {
                fontSize: isMobile ? 10 : 12,
                color: axisColor,
                formatter: (value) => {
                    if (value >= 1000000) {
                        return (value / 1000000).toFixed(1) + 'M';
                    } else if (value >= 1000) {
                        return (value / 1000).toFixed(1) + 'K';
                    }
                    return value;
                }
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                lineStyle: {
                    color: '#f0f0f0',
                    type: 'dashed'
                }
            }
        },
        // Bar spacing configuration
        barCategoryGap: isMobile ? '40%' : '20%',
        barGap: isMobile ? '10%' : '5%',
        color: ["#5470C6", "#91CC75", "#EE6666", "#FAC858"],
        series: [
            {
                name: "First Click",
                type: "bar",
                data: filteredData.map(
                    (item) => Math.round(item.attribution_first_click_heuristic)
                ),
                emphasis: {
                    focus: 'series'
                }
            },
            {
                name: "Last Click",
                type: "bar",
                data: filteredData.map(
                    (item) => Math.round(item.attribution_last_click_heuristic)
                ),
                emphasis: {
                    focus: 'series'
                }
            },
            {
                name: "Data Attribution 1",
                type: "bar",
                data: filteredData.map(
                    (item) => Math.round(item.attribution_markov_algorithmic)
                ),
                emphasis: {
                    focus: 'series'
                }
            },
            {
                name: "Data Attribution 2",
                type: "bar",
                data: filteredData.map(
                    (item) => Math.round(item.attribution_shapley_size4_conv_rate_algorithmic)
                ),
                emphasis: {
                    focus: 'series'
                }
            },
        ],
    };

    const onChartClick = (params) => {
        const clickedChannel = params.name;
        setSelectedChannel((prev) =>
            prev === clickedChannel ? null : clickedChannel
        );
    };

    return (
        <div className="p-2 sm:p-4">
            <h3 className="text-lg sm:text-xl font-semibold mb-4 text-center sm:text-left">
                Attribution Models by Channel
            </h3>
            <div className="w-full overflow-hidden">
                <ReactECharts
                    option={option}
                    style={{
                        height: isMobile ? '400px' : '500px',
                        width: '100%'
                    }}
                    onEvents={{ click: onChartClick }}
                    className="w-full"
                    opts={{
                        renderer: 'canvas', // Better performance on mobile
                        devicePixelRatio: window.devicePixelRatio || 1
                    }}
                />
            </div>
            {/* {isMobile && filteredData.length > 6 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                    Pinch to zoom or swipe to explore the chart
                </p>
            )} */}
        </div>
    );
};

export default ChannelAttributionChart;